import { Commands, LevelPhase, LevelSim, SimConstants, TargetPriority, Tower, isHex } from "@highmarch/domain";
import type { Command } from "@highmarch/domain";
import { icon } from "./iconsKit";
import { bindSeg, segHtml, starsHtml, statHtml } from "./kit";
import { sheetHeadHtml } from "./components";
import { portraitImg } from "./portraits";
import { showDialog } from "./dialog";
import { confirmDemolish } from "./demolish";
import { formatRange, gainPercent, sheetUpgrade, shotsPerSecond } from "./upgradePreview";
import { dealtLine } from "./damageReport";
import { sheetFacts } from "./towerFacts";
import { tierFourSheet } from "./tier4Facts";

const PRIORITIES = [
  { key: String(TargetPriority.First), label: "First" }, { key: String(TargetPriority.Last), label: "Last" },
  { key: String(TargetPriority.Strongest), label: "Strong" }, { key: String(TargetPriority.Weakest), label: "Weak" },
];

/** Tower sheet (P1.10.6): parchment sheet with portrait, tier stars, four stat plates, target selector, Demolish and the upgrade line. */
export class TowerInfoView {
  readonly root: HTMLDivElement;
  private towerId = -1;
  private signature = "";

  /**
   * Set by the level: the upgrade button uses the Upgrade card on the offer (`cardIndex`) for this tower — picking it
   * hands over to the card's select-then-confirm flow with the tower already chosen.
   */
  onUpgrade: ((towerId: number, cardIndex: number) => void) | null = null;
  /** Set by the level: the run's damage per tower id (LevelSession.damageByTower), for the sheet's "Dealt" line (R2). */
  damage: ReadonlyMap<number, number> | null = null;
  /** Set by the level: kills under each Hex Totem's curse (LevelSession.assistsByTower): a totem's KILLS. */
  assists: ReadonlyMap<number, number> | null = null;
  private killsOf(t: Tower, hex: boolean): number { return hex ? this.assists?.get(t.id) ?? 0 : t.kills; }

  constructor(private readonly parent: HTMLElement, private readonly execute: (c: Command) => void, private readonly onClose: () => void) {
    this.root = document.createElement("div");
    this.root.id = "tower-info";
    this.root.className = "sheet panel hidden";
    parent.append(this.root);
  }

  get openTowerId(): number { return this.towerId; }
  get isOpen(): boolean { return this.towerId >= 0; }

  open(t: Tower): void { this.towerId = t.id; }
  close(): void { this.towerId = -1; this.signature = ""; this.root.classList.add("hidden"); this.root.innerHTML = ""; this.onClose(); }

  refresh(sim: LevelSim): void {
    if (this.towerId < 0) return;
    const t = sim.findTower(this.towerId);
    if (t === null) { this.close(); return; }
    const a = sim.balance.towers[t.archetype];
    const tiers = a.tiers.length;
    const dmg = sim.damageOf(t);
    const interval = sim.fireIntervalOf(t);
    const dps = dmg.toNumber() * shotsPerSecond(interval);
    const targets = a.targetsGround && a.targetsAir ? "ground + air" : a.targetsAir ? "air only" : "ground only";
    const disabled = t.disabledTicksLeft > 0 ? Math.ceil(t.disabledTicksLeft / SimConstants.TicksPerSecond) : 0;
    const canSell = !a.isBase && sim.phase !== LevelPhase.WaveRunning;
    const up = sheetUpgrade(sim, t);
    const t4 = tierFourSheet(sim, t, 60);                 // tier IV (M21): the branch's name, art, identity and skills
    const signature = `${t.id}|${t.tier}|${t.priority}|${disabled}|${canSell}|${sim.phase}|${up.cardIndex}|${up.sub}|${t4.key}`;
    if (signature === this.signature && !this.root.classList.contains("hidden")) {
      // Kills tick up mid-wave: write the number into the existing stat rather than rebuilding the sheet (and its portrait).
      const kills = this.root.querySelector<HTMLElement>(".stats4 .stat:last-child b");
      const k = String(this.killsOf(t, isHex(a)));
      if (kills && kills.textContent !== k) kills.textContent = k;
      const dealt = this.root.querySelector<HTMLElement>(".dealt");
      const line = this.damage ? dealtLine(this.damage, t.id) : "";
      if (dealt && dealt.innerHTML !== line) dealt.innerHTML = line;
      // the TOWERS_V2 line carries live numbers (soldiers standing, raiders cursed now)
      const v2 = this.root.querySelector<HTMLElement>(".v2line"), fresh = sheetFacts(sim, t).line ?? "";
      if (v2 && v2.textContent !== fresh) v2.textContent = fresh;
      return;
    }
    this.signature = signature;
    this.root.classList.remove("hidden");
    const v2 = sheetFacts(sim, t);
    const dmgPlate = v2.damage ? statHtml(v2.damage[0], v2.damage[1]) : statHtml("DAMAGE", dmg.toDecimalString(0));
    const dpsValue = v2.dps ?? (interval > 0 ? dps.toFixed(0) : a.auraDamageBonus.isPositive() ? "aura" : "—");
    // Each action is a short label over a small line saying what it costs or needs: Demolish over "Frees the cell · no
    // refund" (it refunds nothing — there is no in-level currency), the upgrade over where its card comes from. The old
    // "Upgrade card › Tier 2" wrapped to three ragged pieces in half a 375 px sheet.
    this.root.innerHTML = `
      ${sheetHeadHtml({ title: t4.title ?? a.name, tint: "blue", portrait: t4.portrait ?? portraitImg(a.id, 60, icon("tower", 34, "#fff8e6"), "", t.tier),
        sub: `Tier ${starsHtml(t.tier, Math.max(tiers, t.tier), 16)} · ${targets}${disabled ? ` · <span class="danger">knocked out ${disabled} s</span>` : ""}` })}
      <div class="stats4">${dmgPlate}${statHtml("RANGE", formatRange(sim.rangeOf(t).toNumber()))}${statHtml("DPS", dpsValue)}${statHtml("KILLS", this.killsOf(t, isHex(a)))}</div>
      ${v2.line ? `<div class="dealt v2line">${v2.line}</div>` : ""}
      ${t4.html}
      ${this.damage && (interval > 0 || v2.damage) ? `<div class="dealt">${dealtLine(this.damage, t.id)}</div>` : ""}
      ${a.isBase ? `<div class="dealt troops">Troops ${gainPercent(sim.troops.strength.toNumber())} health &amp; damage</div>` : ""}
      ${interval > 0 && v2.priority ? `<div class="row target"><span class="lbl">Target</span>${segHtml(PRIORITIES, String(t.priority), "Target priority")}</div>` : ""}
      <div class="row two">
        <button class="btn ${a.isBase ? "dark" : "danger"} disp act demolish" ${canSell ? "" : "disabled"}><span>${a.isBase ? "Stands firm" : "Demolish"}</span><small>${a.isBase ? "Never torn down" : canSell ? "Frees the spot · nothing back" : "Between waves only"}</small></button>
        <button class="btn blue disp act upgrade" ${up.cardIndex >= 0 ? "" : "disabled"}><span>${up.label}</span><small>${up.sub}</small></button>
      </div>`;
    this.root.querySelector<HTMLButtonElement>(".close")!.onclick = () => this.close();
    bindSeg(this.root, (k) => this.execute(Commands.setPriority(t.id, Number(k) as TargetPriority)));
    if (canSell) this.root.querySelector<HTMLButtonElement>(".demolish")!.onclick = () => this.demolish(t, a.name);
    if (up.cardIndex >= 0) this.root.querySelector<HTMLButtonElement>(".upgrade")!.onclick = () => this.onUpgrade?.(t.id, up.cardIndex);
  }

  /**
   * Demolish asks first (the shared confirm dialog, as QUIT and ERASE do): a stray tap on a one-tap button used to
   * throw a tower away. A dialog rather than press-and-hold because the hold is invisible until someone tells you
   * about it, and the sheet already puts the button a deliberate tap away.
   */
  private demolish(t: Tower, name: string): void {
    void confirmDemolish((o) => showDialog(this.parent, o), this.execute, t.id, name, t.tier).then((done) => { if (done && this.towerId === t.id) this.close(); });
  }

  dispose(): void { this.root.remove(); }
}
